import type { FormField } from '@/types/form';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FileUploadField } from './field-types/FileUploadField';

interface FieldPreviewProps {
  field: FormField;
  value?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
  className?: string;
}

export const FieldPreview: React.FC<FieldPreviewProps> = ({
  field,
  value,
  onChange,
  disabled = true, // Preview is disabled by default
  className,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.value);
    }
  };

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    if (!disabled && onChange) {
      const currentValue = Array.isArray(value) ? value : [];
      if (checked) {
        onChange([...currentValue, optionValue]);
      } else {
        onChange(currentValue.filter((v: string) => v !== optionValue));
      }
    }
  };

  const handleRadioChange = (optionValue: string) => {
    if (!disabled && onChange) {
      onChange(optionValue);
    }
  };

  const renderField = () => {
    switch (field.type) {
      case 'input':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <Input
            type={field.type === 'input' ? 'text' : field.type}
            placeholder={field.placeholder}
            defaultValue={field.default as string}
            disabled={disabled}
            onChange={handleInputChange}
            className="w-full"
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder}
            defaultValue={field.default as string}
            disabled={disabled}
            onChange={handleInputChange}
            className={cn(
              "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              disabled && "resize-none"
            )}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder}
            defaultValue={field.default as number}
            disabled={disabled}
            onChange={handleInputChange}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full"
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            defaultValue={field.default as string}
            disabled={disabled}
            onChange={handleInputChange}
            className="w-full"
          />
        );

      case 'select':
        return (
          <select
            defaultValue={field.default as string}
            disabled={disabled}
            onChange={handleInputChange}
            className={cn(
              "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <option value="">Choose an option...</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  defaultChecked={field.default === option.value}
                  disabled={disabled}
                  onChange={() => handleRadioChange(option.value)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  value={option.value}
                  defaultChecked={Array.isArray(field.default) && field.default.includes(option.value)}
                  disabled={disabled}
                  onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'file':
        return (
          <FileUploadField
            field={field}
            value={value}
            onChange={onChange}
            preview={!disabled}
          />
        );

      default:
        return (
          <div className="text-sm text-muted-foreground italic">
            Preview not available for {field.type}
          </div>
        );
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {disabled && (
          <Badge variant="secondary" className="text-xs">
            Preview
          </Badge>
        )}
      </div>
      
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
      
      <div className="relative">
        {renderField()}
        {disabled && (
          <div className="absolute inset-0 cursor-not-allowed" />
        )}
      </div>
      
      {field.validation?.message && (
        <p className="text-xs text-muted-foreground">
          {field.validation.message}
        </p>
      )}
    </div>
  );
};